import { Muxer, ArrayBufferTarget } from "mp4-muxer";

// Log rows are sampled at 25Hz by the FuelTech logger
const FPS = 25;
const WIDTH = 1080;
const HEIGHT = 1920;

const FONT = '"Patopian 1986", "Arial Narrow", sans-serif';

function roundToClosest(x, base = 50) {
  return base * Math.round(parseFloat(x) / base);
}

// Big outlined readout that stays legible over any footage
function drawDigital(ctx, value, label) {
  ctx.lineJoin = "round";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  ctx.font = `italic 900 120px ${FONT}`;
  ctx.strokeStyle = "black";
  ctx.lineWidth = 12;
  ctx.strokeText(value, 14, 106);
  ctx.fillStyle = "white";
  ctx.fillText(value, 14, 106);

  ctx.font = `italic 900 44px ${FONT}`;
  ctx.lineWidth = 8;
  ctx.strokeText(label, 18, 158);
  ctx.fillStyle = "#e30613";
  ctx.fillText(label, 18, 158);
}

const ACCENT = "#e30613";
const GLASS = "rgba(8, 10, 14, 0.82)";
const HAIRLINE = "rgba(255, 255, 255, 0.18)";
const TICK_FONT = '"Roboto Condensed", "Arial Narrow", Roboto, sans-serif';

// 270° sweep starting bottom-left, like an automotive cluster
function dialAngle(t) {
  return ((135 + 270 * t) * Math.PI) / 180;
}

function drawDial(ctx, { value, min = 0, max, redFrom, majorStep, minorStep, tickLabel, unit, sublabel }) {
  const cx = 256;
  const cy = 256;
  const v = Math.min(Math.max(parseFloat(value) || 0, min), max);
  const t = (v - min) / (max - min);

  // smoked-glass face
  ctx.beginPath();
  ctx.arc(cx, cy, 244, 0, Math.PI * 2);
  ctx.fillStyle = GLASS;
  ctx.fill();
  ctx.strokeStyle = HAIRLINE;
  ctx.lineWidth = 3;
  ctx.stroke();

  // redline band near the rim
  if (redFrom != null) {
    ctx.beginPath();
    ctx.arc(cx, cy, 232, dialAngle((redFrom - min) / (max - min)), dialAngle(1));
    ctx.strokeStyle = "rgba(227, 6, 19, 0.85)";
    ctx.lineWidth = 12;
    ctx.stroke();
  }

  // ticks
  const tickCount = Math.round((max - min) / minorStep);
  for (let k = 0; k <= tickCount; k++) {
    const m = min + k * minorStep;
    const major = Math.abs(m / majorStep - Math.round(m / majorStep)) < 1e-6;
    const a = dialAngle((m - min) / (max - min));
    const r0 = major ? 198 : 212;
    const r1 = 224;
    ctx.beginPath();
    ctx.moveTo(cx + r0 * Math.cos(a), cy + r0 * Math.sin(a));
    ctx.lineTo(cx + r1 * Math.cos(a), cy + r1 * Math.sin(a));
    ctx.strokeStyle = major ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.35)";
    ctx.lineWidth = major ? 6 : 3;
    ctx.stroke();
  }

  // numerals
  ctx.font = `bold 34px ${TICK_FONT}`;
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const majorCount = Math.round((max - min) / majorStep);
  for (let k = 0; k <= majorCount; k++) {
    const m = min + k * majorStep;
    const a = dialAngle((m - min) / (max - min));
    ctx.fillText(tickLabel(m), cx + 162 * Math.cos(a), cy + 162 * Math.sin(a));
  }

  ctx.font = `bold 22px ${TICK_FONT}`;
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.fillText(sublabel, cx, cy - 64);

  // live value arc around the hub — anchored at zero so vacuum sweeps one
  // way and boost the other
  const zeroT = (0 - min) / (max - min);
  if (Math.abs(t - zeroT) > 0.005) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, 126, dialAngle(Math.min(zeroT, t)), dialAngle(Math.max(zeroT, t)));
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 9;
    ctx.lineCap = "round";
    ctx.shadowColor = "rgba(227, 6, 19, 0.8)";
    ctx.shadowBlur = 12;
    ctx.stroke();
    ctx.restore();
  }

  // needle
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(dialAngle(t));
  ctx.shadowColor = "rgba(227, 6, 19, 0.55)";
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.moveTo(-30, 0);
  ctx.lineTo(0, -8);
  ctx.lineTo(192, 0);
  ctx.lineTo(0, 8);
  ctx.closePath();
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.restore();

  // hub
  ctx.beginPath();
  ctx.arc(cx, cy, 20, 0, Math.PI * 2);
  ctx.fillStyle = "#0b0d11";
  ctx.fill();
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 4;
  ctx.stroke();

  // digital readout
  ctx.font = `italic 900 68px ${FONT}`;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(unit.format(v), cx, cy + 100);
  ctx.font = `italic 900 26px ${FONT}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText(unit.label, cx, cy + 148);
}

// Registry of everything the app knows how to draw. `columnMatch` is checked
// against the CSV header (case-insensitive substring); editor previews are
// generated from `draw` with `sample`.
export const GAUGES = {
  "rpm-text": {
    name: "RPM (text)",
    width: 460,
    height: 170,
    columnMatch: ["rpm"],
    sample: 6250,
    draw(ctx, value) {
      drawDigital(ctx, String(roundToClosest(value, 10)), "RPM");
    },
  },
  "speed-text": {
    name: "Speed (text)",
    width: 460,
    height: 170,
    columnMatch: ["velocidade"],
    sample: 137,
    draw(ctx, value) {
      drawDigital(ctx, String(roundToClosest(value, 1)), "KM/H");
    },
  },
  "boost-text": {
    name: "Boost (text)",
    width: 460,
    height: 170,
    columnMatch: ["map"],
    sample: 1.2,
    draw(ctx, value) {
      const v = parseFloat(value) || 0;
      drawDigital(ctx, `${v > 0 ? "+" : ""}${v.toFixed(2)}`, "BOOST · BAR");
    },
  },
  tachometer: {
    name: "Tachometer",
    width: 512,
    height: 512,
    columnMatch: ["rpm"],
    sample: 4550,
    draw(ctx, value) {
      drawDial(ctx, {
        value,
        max: 8000,
        redFrom: 5500,
        majorStep: 1000,
        minorStep: 200,
        tickLabel: (m) => String(m / 1000),
        sublabel: "RPM ×1000",
        unit: { format: (v) => String(roundToClosest(v, 10)), label: "RPM" },
      });
    },
  },
  speedometer: {
    name: "Speedometer",
    width: 512,
    height: 512,
    columnMatch: ["velocidade"],
    sample: 137,
    draw(ctx, value) {
      drawDial(ctx, {
        value,
        max: 280,
        redFrom: null,
        majorStep: 40,
        minorStep: 20,
        tickLabel: (m) => String(m),
        sublabel: "KM/H",
        unit: { format: (v) => String(roundToClosest(v, 1)), label: "KM/H" },
      });
    },
  },
  boost: {
    name: "Boost (MAP)",
    width: 512,
    height: 512,
    columnMatch: ["map"],
    sample: 1.2,
    draw(ctx, value) {
      drawDial(ctx, {
        value,
        min: -1,
        max: 2,
        redFrom: 1.5,
        majorStep: 1,
        minorStep: 0.25,
        tickLabel: (m) => String(m),
        sublabel: "BOOST · BAR",
        unit: {
          format: (v) => `${v > 0 ? "+" : ""}${v.toFixed(2)}`,
          label: "BAR",
        },
      });
    },
  },
  gear: {
    name: "Gear",
    width: 200,
    height: 240,
    columnMatch: ["marcha", "gear"],
    sample: "3",
    draw(ctx, rawValue) {
      const trimmed = String(rawValue).trim();
      const value =
        trimmed === "" || trimmed === "0" || trimmed.toUpperCase() === "N"
          ? "N"
          : trimmed;
      ctx.beginPath();
      ctx.roundRect(3, 3, 194, 234, 26);
      ctx.fillStyle = GLASS;
      ctx.fill();
      ctx.strokeStyle = HAIRLINE;
      ctx.lineWidth = 3;
      ctx.stroke();

      ctx.font = `italic 900 24px ${FONT}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      ctx.fillStyle = ACCENT;
      ctx.fillText("GEAR", 22, 42);

      const neutral = value === "N";
      ctx.font = `italic 900 150px ${FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = neutral ? "rgba(255,255,255,0.45)" : "#ffffff";
      ctx.fillText(String(value), 100, 140);
    },
  },
};

export const GAUGE_LIST = Object.entries(GAUGES).map(([id, g]) => ({
  id,
  ...g,
}));

const previewCache = {};

// Editor/sidebar image for a gauge: its static asset, or a one-off render of
// the painter with a sample value for image-less (text) gauges.
export function gaugePreview(id) {
  const gauge = GAUGES[id];
  if (gauge.img) return gauge.img;
  if (!previewCache[id]) {
    const canvas = document.createElement("canvas");
    canvas.width = gauge.width;
    canvas.height = gauge.height;
    gauge.draw(canvas.getContext("2d"), gauge.sample, null);
    previewCache[id] = canvas.toDataURL();
  }
  return previewCache[id];
}

export function parseLog(log) {
  const lines = log.trim().split(/\r?\n/);
  const header = lines[0].split(",").map((h) => h.trim());
  const rows = lines
    .slice(1)
    .map((line) => line.split(","))
    .filter((row) => row.length >= header.length);

  const columns = {};
  for (const [id, gauge] of Object.entries(GAUGES)) {
    columns[id] = header.findIndex((h) =>
      gauge.columnMatch.some((c) => h.toLowerCase().includes(c.toLowerCase()))
    );
  }

  return { rows, columns };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

async function loadImages() {
  return { bg: await loadImage("/overlay-bg.png") };
}

function createCanvas(width, height) {
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function seekTo(video, t) {
  return new Promise((resolve) => {
    if (Math.abs(video.currentTime - t) < 1e-4) return resolve();
    video.onseeked = () => resolve();
    video.currentTime = t;
  });
}

// Scale-to-fill placement of the camera frame on the output canvas
function coverFit(srcW, srcH, dstW, dstH) {
  const s = Math.max(dstW / srcW, dstH / srcH);
  return {
    x: (dstW - srcW * s) / 2,
    y: (dstH - srcH * s) / 2,
    width: srcW * s,
    height: srcH * s,
  };
}

// Decode the clip's audio track and check we can re-encode it as AAC.
// Returns null (silent output) when the clip has no audio or AAC encoding
// isn't available — never blocks the render.
async function prepareAudio(file) {
  if (typeof window.AudioEncoder === "undefined") return null;
  let audioCtx;
  try {
    const buf = await file.arrayBuffer();
    audioCtx = new AudioContext();
    const audioBuffer = await audioCtx.decodeAudioData(buf);
    const numberOfChannels = Math.min(2, audioBuffer.numberOfChannels);
    const config = {
      codec: "mp4a.40.2",
      sampleRate: audioBuffer.sampleRate,
      numberOfChannels,
      bitrate: 128_000,
    };
    const { supported } = await window.AudioEncoder.isConfigSupported(config);
    return supported ? { audioBuffer, config, numberOfChannels } : null;
  } catch {
    return null;
  } finally {
    audioCtx?.close();
  }
}

async function encodeAudio(muxer, { audioBuffer, config, numberOfChannels }) {
  let audioError = null;
  const encoder = new window.AudioEncoder({
    output: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
    error: (e) => {
      audioError = e;
    },
  });
  encoder.configure(config);

  const sampleRate = config.sampleRate;
  const step = Math.floor(sampleRate / 10);
  for (let start = 0; start < audioBuffer.length; start += step) {
    const frames = Math.min(step, audioBuffer.length - start);
    const data = new Float32Array(frames * numberOfChannels);
    for (let c = 0; c < numberOfChannels; c++) {
      data.set(
        audioBuffer.getChannelData(c).subarray(start, start + frames),
        c * frames
      );
    }
    const audioData = new window.AudioData({
      format: "f32-planar",
      sampleRate,
      numberOfFrames: frames,
      numberOfChannels,
      timestamp: Math.round((start / sampleRate) * 1e6),
      data,
    });
    encoder.encode(audioData);
    audioData.close();
  }
  await encoder.flush();
  encoder.close();
  if (audioError) throw audioError;
}

export async function renderVideo({ gauges, log, videoFile, offset = 0, onProgress }) {
  if (typeof window.VideoEncoder === "undefined") {
    throw new Error(
      "This browser doesn't support WebCodecs — use a recent Chrome or Edge."
    );
  }
  if (!log) {
    throw new Error("Load a log file first.");
  }

  const { rows, columns } = parseLog(log);
  const images = await loadImages();

  let videoEl = null;
  let audio = null;
  if (videoFile) {
    videoEl = document.createElement("video");
    videoEl.src = URL.createObjectURL(videoFile);
    videoEl.muted = true;
    await new Promise((resolve, reject) => {
      videoEl.onloadedmetadata = resolve;
      videoEl.onerror = () => reject(new Error("Couldn't load the video file"));
    });
    audio = await prepareAudio(videoFile);
  }

  // Resolve each enabled gauge to a painter + its data column and placement
  const activeGauges = [];
  for (const gauge of gauges) {
    const painter = GAUGES[gauge.id];
    if (!painter) continue;
    const column = columns[gauge.id];
    if (column === -1) {
      console.warn(`Log has no column for gauge "${gauge.id}", skipping`);
      continue;
    }
    activeGauges.push({
      painter,
      column,
      x: gauge.x,
      y: gauge.y,
      width: gauge.width ?? painter.width,
      height: gauge.height ?? painter.height,
      scratch: createCanvas(painter.width, painter.height),
    });
  }

  const canvas = createCanvas(WIDTH, HEIGHT);
  const ctx = canvas.getContext("2d");

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: "avc", width: WIDTH, height: HEIGHT },
    ...(audio
      ? {
          audio: {
            codec: "aac",
            sampleRate: audio.config.sampleRate,
            numberOfChannels: audio.numberOfChannels,
          },
        }
      : {}),
    fastStart: "in-memory",
  });

  if (audio) {
    await encodeAudio(muxer, audio);
  }

  let encoderError = null;
  const encoder = new window.VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => {
      encoderError = e;
    },
  });
  encoder.configure({
    codec: "avc1.420028",
    width: WIDTH,
    height: HEIGHT,
    bitrate: 8_000_000,
    framerate: FPS,
  });

  // With footage the output follows the clip; gauges read the log shifted by
  // the sync offset (videoTime = logTime + offset), frozen at the log's ends.
  const duration = videoEl ? videoEl.duration : rows.length / FPS;
  const totalFrames = Math.ceil(duration * FPS);
  const fit = videoEl
    ? coverFit(videoEl.videoWidth, videoEl.videoHeight, WIDTH, HEIGHT)
    : null;

  const frameDuration = 1e6 / FPS;
  for (let i = 0; i < totalFrames; i++) {
    if (encoderError) throw encoderError;
    const t = i / FPS;

    if (videoEl) {
      await seekTo(videoEl, Math.min(t, duration - 0.001));
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      ctx.drawImage(videoEl, fit.x, fit.y, fit.width, fit.height);
    } else {
      ctx.drawImage(images.bg, 0, 0, WIDTH, HEIGHT);
    }

    const logT = videoEl ? t - offset : t;
    const rowIndex = Math.min(
      Math.max(Math.round(logT * FPS), 0),
      rows.length - 1
    );
    const row = rows[rowIndex];
    for (const g of activeGauges) {
      const sctx = g.scratch.getContext("2d");
      sctx.clearRect(0, 0, g.painter.width, g.painter.height);
      g.painter.draw(sctx, row[g.column], images);
      ctx.drawImage(g.scratch, g.x, g.y, g.width, g.height);
    }

    const frame = new window.VideoFrame(canvas, {
      timestamp: Math.round(i * frameDuration),
      duration: Math.round(frameDuration),
    });
    encoder.encode(frame, { keyFrame: i % (FPS * 2) === 0 });
    frame.close();

    // Backpressure: don't let raw frames pile up faster than they encode
    while (encoder.encodeQueueSize > 15) {
      await new Promise((r) => setTimeout(r, 5));
    }
    if (i % 10 === 0) {
      onProgress?.(i / totalFrames);
      await new Promise((r) => setTimeout(r));
    }
  }

  await encoder.flush();
  encoder.close();
  if (encoderError) throw encoderError;
  muxer.finalize();
  onProgress?.(1);

  return new Blob([muxer.target.buffer], { type: "video/mp4" });
}
